import { Inbox, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Page, PageHeader } from '../components/layout/AppShell';
import { LeadStatusBadge } from '../components/ui/data';
import { useToast } from '../components/ui/overlays';
import { Badge, Button, Card, EmptyState, LoadingBlock } from '../components/ui/primitives';
import { useEmails, useMailboxes, useRunWorker } from '../hooks/queries';
import { formatDateTime, formatRelative, truncate } from '../lib/utils';
import { EmailDrawer } from './EmailsPage';

export const InboxPage = () => {
  const { projectId = '' } = useParams<{ projectId: string }>();
  const { data, isLoading, refetch } = useEmails(projectId, {
    page: 1,
    pageSize: 50,
    direction: 'INBOUND',
  });
  const { data: mailboxes } = useMailboxes(projectId);
  const runWorker = useRunWorker();
  const toast = useToast();
  const [openId, setOpenId] = useState<string | null>(null);

  const replies = data?.items ?? [];
  const mailbox = mailboxes?.find((entry) => entry.isDefault) ?? mailboxes?.[0];

  const sync = async () => {
    try {
      await runWorker.mutateAsync('inbox');
      await refetch();
      toast.success('Inbox synced');
    } catch (error) {
      toast.error(error);
    }
  };

  if (isLoading) return <LoadingBlock />;

  return (
    <Page>
      <PageHeader
        title="Inbox"
        description="Replies pulled over IMAP. A reply moves its lead to Replied and cancels anything still queued for that person."
        actions={
          <Button
            icon={<RefreshCw className="h-4 w-4" />}
            loading={runWorker.isPending}
            onClick={sync}
          >
            Sync now
          </Button>
        }
      />

      {mailbox ? (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-2.5 text-[13px]">
          <span className="text-muted">
            Reading <strong className="text-ink">{mailbox.imapUser ?? mailbox.fromEmail}</strong> ·{' '}
            {mailbox.imapFolder}
          </span>
          <span className="text-faint">
            last sync {mailbox.lastSyncAt ? formatRelative(mailbox.lastSyncAt) : 'never'}
          </span>
          {mailbox.lastSyncError ? (
            <Badge tone="danger">{truncate(mailbox.lastSyncError, 60)}</Badge>
          ) : mailbox.hasImapPassword ? (
            <Badge tone="positive">IMAP configured</Badge>
          ) : (
            <Badge tone="warning">IMAP password missing</Badge>
          )}
        </div>
      ) : null}

      {replies.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Inbox className="h-5 w-5" />}
            title="No replies yet"
            description={
              mailbox?.hasImapPassword
                ? 'The inbox is checked every five minutes. Nothing has come back yet.'
                : 'Add IMAP credentials under Project → Mailbox so replies can be read.'
            }
          />
        </Card>
      ) : (
        <div className="space-y-2">
          {replies.map((reply) => (
            <Card
              key={reply.id}
              className="cursor-pointer p-4 transition-colors hover:border-faint/60"
              onClick={() => setOpenId(reply.id)}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[14px] font-semibold text-ink">
                      {reply.fromName || reply.fromEmail}
                    </span>
                    {reply.lead ? <LeadStatusBadge status={reply.lead.status} /> : null}
                    {reply.lead ? (
                      <span className="text-xs text-faint">{reply.lead.companyName}</span>
                    ) : (
                      <Badge tone="muted">unmatched</Badge>
                    )}
                  </div>
                  <div className="mt-1 text-[13px] font-medium text-ink">{reply.subject}</div>
                  <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-muted">
                    {reply.snippet ?? truncate(reply.bodyText ?? '', 200)}
                  </p>
                </div>
                <span
                  className="shrink-0 text-2xs text-faint"
                  title={formatDateTime(reply.receivedAt)}
                >
                  {formatRelative(reply.receivedAt ?? reply.createdAt)}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <EmailDrawer emailId={openId} projectId={projectId} onClose={() => setOpenId(null)} />
    </Page>
  );
};
